import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatUZS } from '@/lib/format';
import { salesByHour, salesByWeekday, type SaleLike } from '@/lib/report-metrics';
import { ChartTooltipBox, compactUZS } from './chart-bits';

function BarBlock({
  title,
  hint,
  data,
  color,
}: {
  title: string;
  hint: string;
  data: { label: string; sum: number; count: number }[];
  color: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <h3 className="text-sm font-medium text-foreground mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-3">{hint}</p>
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={compactUZS} width={56} />
            <Tooltip
              cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <ChartTooltipBox
                    title={String(label)}
                    rows={[
                      { label: t('tick_reports.sale'), value: formatUZS(Number(payload[0]?.payload?.sum ?? 0)) },
                      { label: t('tick_reports.colTransactions'), value: t('tick_reports.count', { n: payload[0]?.payload?.count ?? 0 }) },
                    ]}
                  />
                ) : null
              }
            />
            <Bar dataKey="sum" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ActivityCharts({ sales }: { sales: SaleLike[] }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <BarBlock
        title={t('tick_reports.hoursTitle')}
        hint={t('tick_reports.hoursHint')}
        data={salesByHour(sales)}
        color="hsl(var(--info))"
      />
      <BarBlock
        title={t('tick_reports.weekdayTitle')}
        hint={t('tick_reports.weekdayHint')}
        data={salesByWeekday(sales)}
        color="hsl(var(--success))"
      />
    </div>
  );
}
